import { css } from '@emotion/react'
import React, { useState } from 'react'
import {
  getCurrentBrandSlug,
  isEmbeddedPersonalPanel,
  navigateWithMomentsBase,
} from '@opallabs/brown-dwarf'
import { Size, Spacing } from '@opallabs/gem'
import { closeDrawer, Drawer, openDrawer } from '@opallabs/kuiper'
import { useOnce } from '@opallabs/lambda'
import { APPROVAL_GLYPH, Icon, TASK_GLYPH } from '@opallabs/lapidary'
import { testFeature } from '@opallabs/principles'
import { Tabs } from '@opallabs/satellite'
import {
  NormalizedApproval,
  NormalizedTask,
  Notification,
  TabStatusKeys,
} from '@opallabs/wormhole/types'
import { ConnectedMomentPreviewModal } from '../MomentPreviewModal/ConnectedMomentPreviewModal'
import { ConnectedApprovalPanel } from './ApprovalPanel/ConnectedApprovalPanel'
import {
  NormalizedApprovalToPreviewWorkflowItem,
  NormalizedTaskToPreviewWorkflowItem,
} from './helpers'
import { ConnectedDownloadPanel } from './NotificationsPanel/ConnectedDownloadPanel'
import { ConnectedNotificationPanel } from './NotificationsPanel/ConnectedNotificationPanel'
import * as sharedStyles from './Shared/styles'
import * as styles from './styles'
import { ConnectedTaskPanel } from './TaskPanel/ConnectedTaskPanel'
import { MenuOption, PersonalPanelProps, Runnable } from './types'
import UserHeader from './UserHeader'
import { useSelectedPrimaryTab } from './useSelectedPrimaryTab'
import { useUrlFragment } from './useUrlFragment'

const TabText = ({
  glyph,
  title,
}: {
  glyph: string
  title: string
}): JSX.Element => {
  const size = glyph.includes('gem-') ? Size.Icon : Size.Icon * 0.8125

  return (
    <React.Fragment>
      <Icon
        ariaHidden
        height={size}
        glyph={glyph}
        styleOverrides={{
          container: {
            marginInlineEnd: Spacing.XS,
          },
        }}
        useHypernova={!glyph.includes('gem-')}
        width={size}
      />
      {title}
    </React.Fragment>
  )
}

/**
 * Navigate to a moment's post page.
 * When embedded in an iframe (Asgard), navigates the parent window.
 * Otherwise uses backbone router or direct navigation.
 */
const navigateToMoment = (
  momentId: number | string,
  contentId?: number | string
): void => {
  const brandSlug = getCurrentBrandSlug()
  if (!brandSlug || !momentId) return

  const url = contentId
    ? `/moments/${brandSlug}/${momentId}/posts/${contentId}`
    : `/moments/${brandSlug}/${momentId}`

  // When loaded as standalone content in Asgard's PersonalPanel drawer,
  // open in a new tab to preserve the current view
  // The #close_window hash tells the moment shroud to close the tab when done
  if (isEmbeddedPersonalPanel()) {
    window.open(`${url}#close_window`, '_blank')
    return
  }

  // Use MomentsBase router if available, otherwise direct navigation
  navigateWithMomentsBase(url, { trigger: true })
}

export const PersonalPanel: React.FC<PersonalPanelProps> = ({
  getStatus,
  isViewOnlyRole,
  onMenuClick,
  onNotificationClick,
  onTogglePersonalPanel,
  status,
  theme = 'light',
  toggleLeftHandPreview,
  togglePersonalPanel,
  user,
}) => {
  const forceOpen = (): void => {
    openDrawer('darwin')
    togglePersonalPanel(true)
  }

  const { initialTab } = useUrlFragment({ callback: forceOpen })

  useOnce(getStatus)

  const [modalVisible, setModalVisible] = useState(false)
  const [activeAssignment, setActiveAssignment] = useState<
    NormalizedTask | NormalizedApproval | undefined
  >(undefined)

  const isMomentsApp = window.Opal?.State?.current_application === 'moments'

  // Check if we're loaded as standalone content in Asgard's PersonalPanel drawer
  // This is indicated by the open_personal_panel query param that Asgard adds
  // When true, we need to communicate with Asgard via postMessage
  // When false, we're part of a full monolith page that handles PersonalPanel natively
  const isInAsgardPersonalPanel = isEmbeddedPersonalPanel()

  const { onTabChange, selectedIndex } = useSelectedPrimaryTab({
    initialTab,
    isMomentsApp,
    isViewOnlyRole,
    tabChangeCallback: () => toggleLeftHandPreview(null),
  })

  const handleMenuClick = (menuItem: MenuOption): void => {
    // When embedded in Asgard iframe, the menu click handler in view.coffee opens
    // settings/profile in new tabs, so we don't need to close the drawer
    if (!isInAsgardPersonalPanel) {
      closeDrawer('darwin')
    }
    onMenuClick(menuItem)
  }

  const onOpen: Runnable = () => {
    togglePersonalPanel(true)
    onTogglePersonalPanel(false, true)
  }

  const onClose: Runnable = () => {
    // When in Asgard PersonalPanel drawer, just notify parent to close
    // Don't close Darwin's panel - let it stay loaded for faster subsequent opens
    if (isInAsgardPersonalPanel) {
      window.parent.postMessage(
        {
          message: 'asgard:route:backbone_execute',
          route: 'close-personal-panel',
        },
        '*'
      )
      return
    }

    togglePersonalPanel(false)
    onTogglePersonalPanel(true, false)
    toggleLeftHandPreview(null)
    setModalVisible(false)
  }

  const setApprovalPreviewCard: (
    approval: NormalizedApproval | null
  ) => void = (approval) => {
    if (approval === null) {
      // following pattern of backbone function call
      // null implies that we should close all previews
      setModalVisible(false)
      setActiveAssignment(undefined)
      toggleLeftHandPreview(null)
      return
    }

    // When embedded in Asgard iframe, navigate to the moment instead of
    // showing constrained preview modals
    if (isInAsgardPersonalPanel && approval.momentId !== undefined) {
      navigateToMoment(approval.momentId, approval.contentId)
      return
    }

    if (approval.entityType === 'phase_item') {
      toggleLeftHandPreview(NormalizedApprovalToPreviewWorkflowItem(approval))
      setModalVisible(false)
      setActiveAssignment(undefined)
    } else if (approval.entityType === 'assignment') {
      if (approval.momentId !== undefined) {
        setActiveAssignment(approval)
        setModalVisible(true)
      }
      toggleLeftHandPreview(null)
    }
  }

  const setTaskPreviewCard: (task: NormalizedTask | null) => void = (task) => {
    if (task === null) {
      // following pattern of backbone function call
      // null implies that we should close all previews
      setModalVisible(false)
      setActiveAssignment(undefined)
      toggleLeftHandPreview(null)
      return
    }

    // When embedded in Asgard iframe, navigate to the moment instead of
    // showing constrained preview modals
    if (isInAsgardPersonalPanel && task.momentId !== undefined) {
      navigateToMoment(task.momentId, task.contentId)
      return
    }

    if (task.entityType === 'phase_item') {
      toggleLeftHandPreview(NormalizedTaskToPreviewWorkflowItem(task))
      setModalVisible(false)
      setActiveAssignment(undefined)
    } else if (task.entityType === 'assignment') {
      if (task.momentId !== undefined) {
        setActiveAssignment(task)
        setModalVisible(true)
      }
      toggleLeftHandPreview(null)
    }
  }

  const handleNotificationClick = (notification: Notification): void => {
    // When embedded in Asgard iframe, links open in a new tab so don't close the drawer
    if (!isInAsgardPersonalPanel) {
      closeDrawer('darwin')
      onClose()
    }
    onNotificationClick(notification)
  }

  const tabHasUnseen = (tab: TabStatusKeys): boolean =>
    status !== null && status[tab].unseen > 0

  if (!user) return null

  return (
    <React.Fragment>
      <Drawer
        hotKey="shift+a"
        key={`${status ? 'has-status' : 'no-status'}-personal-panel`}
        name="darwin"
        onClose={onClose}
        onOpen={onOpen}
        withoutOverlay={isInAsgardPersonalPanel}
      >
        <div css={css(sharedStyles.drawer({ theme }))}>
          <div css={css(sharedStyles.userHeader)}>
            <UserHeader
              companyName={user.companyName}
              name={user.name}
              onClickClose={(): void => {
                // When in Asgard PersonalPanel drawer, just notify parent to close
                // Don't close Darwin's panel - let it stay loaded for faster subsequent opens
                if (isInAsgardPersonalPanel) {
                  window.parent.postMessage(
                    {
                      message: 'asgard:route:backbone_execute',
                      route: 'close-personal-panel',
                    },
                    '*'
                  )
                } else {
                  closeDrawer('darwin')
                }
              }}
              onMenuClick={handleMenuClick}
              theme={theme}
              userId={user.id}
            />
          </div>
          <Tabs
            onSelect={onTabChange}
            selectedIndex={selectedIndex}
            styleOverrides={{
              tabsWrapper: sharedStyles.mainTabWrapper({ theme }),
            }}
          >
            <Tabs.List
              styleOverrides={{ tabList: sharedStyles.mainTabList({ theme }) }}
            >
              {isMomentsApp && !isViewOnlyRole && (
                <React.Fragment>
                  {testFeature('workflow') && (
                    <Tabs.Tab
                      styleOverrides={{
                        baseTab: styles.mainTab({
                          hasUnseen: tabHasUnseen('tasks'),
                          theme,
                        }),
                        selectedTab: styles.mainTabSelected({ theme }),
                      }}
                    >
                      <TabText glyph={TASK_GLYPH} title="Tasks" />
                    </Tabs.Tab>
                  )}
                  <Tabs.Tab
                    styleOverrides={{
                      baseTab: styles.mainTab({
                        hasUnseen: tabHasUnseen('approvals'),
                        theme,
                      }),
                      selectedTab: styles.mainTabSelected({ theme }),
                    }}
                  >
                    <TabText glyph={APPROVAL_GLYPH} title="Approvals" />
                  </Tabs.Tab>
                </React.Fragment>
              )}
              <Tabs.Tab
                styleOverrides={{
                  baseTab: styles.mainTab({
                    hasUnseen: tabHasUnseen('notifications'),
                    theme,
                  }),
                  selectedTab: styles.mainTabSelected({ theme }),
                }}
              >
                <TabText glyph="gem-bell" title="Notifications" />
              </Tabs.Tab>
              <Tabs.Tab
                styleOverrides={{
                  baseTab: styles.mainTab({
                    hasUnseen: tabHasUnseen('downloads'),
                    theme,
                  }),
                  selectedTab: styles.mainTabSelected({ theme }),
                }}
              >
                <TabText glyph="gem-download" title="Downloads" />
              </Tabs.Tab>
            </Tabs.List>
            {isMomentsApp && !isViewOnlyRole && (
              <React.Fragment>
                {testFeature('workflow') && (
                  <Tabs.Panel
                    styleOverrides={{
                      tabPanel: sharedStyles.mainTabPanel({ theme }),
                    }}
                    theme={theme}
                  >
                    <ConnectedTaskPanel
                      setTaskPreviewCard={setTaskPreviewCard}
                      status={status}
                      user={user}
                    />
                  </Tabs.Panel>
                )}
                <Tabs.Panel
                  styleOverrides={{
                    tabPanel: sharedStyles.mainTabPanel({ theme }),
                  }}
                  theme={theme}
                >
                  <ConnectedApprovalPanel
                    setApprovalPreviewCard={setApprovalPreviewCard}
                    status={status}
                    user={user}
                  />
                </Tabs.Panel>
              </React.Fragment>
            )}
            <Tabs.Panel
              styleOverrides={{
                tabPanel: sharedStyles.mainTabPanel({ theme }),
              }}
              theme={theme}
            >
              <ConnectedNotificationPanel
                onNotificationClick={handleNotificationClick}
                theme={theme}
                type="notification"
              />
            </Tabs.Panel>
            <Tabs.Panel
              styleOverrides={{
                tabPanel: sharedStyles.mainTabPanel({ theme }),
              }}
              theme={theme}
            >
              <ConnectedDownloadPanel
                onNotificationClick={onNotificationClick}
                theme={theme}
                type="download"
              />
            </Tabs.Panel>
          </Tabs>
        </div>
      </Drawer>
      {activeAssignment !== undefined ? (
        <ConnectedMomentPreviewModal
          assignmentType={activeAssignment.assignmentType}
          closeDrawer={(): void => closeDrawer('darwin')}
          closeMomentPreview={(): void => setModalVisible(false)}
          modalVisible={modalVisible}
          momentId={
            activeAssignment.momentId
              ? activeAssignment.momentId.toString()
              : ''
          }
          stageName={
            activeAssignment.phaseName ? activeAssignment.phaseName : 'No Phase'
          }
        />
      ) : null}
    </React.Fragment>
  )
}
